/**
 * THE CLIENT-LOADED DOCTRINE (`SPEC-APP.md` §3.1). Fetches EVERY page of the register's list
 * operation at `limit=500` and concatenates them; AG Grid then sorts, filters and quick-filters
 * client-side. THROWS IN DEV when the concatenated length does not equal the server's `total` —
 * a silently short register is the one failure this machine must never hide.
 *
 * The doctrine is a fact of the config, never guessed from the row count.
 *
 * `clientListOptions` is the shared read: the pickers and the reference lookup subscribe to the
 * SAME query key as the register, so a site list is fetched once per tenant however many controls
 * show it.
 */
import { queryOptions, useQuery } from "@tanstack/react-query";

import { api } from "../api";
import type { OperationId, Page } from "../client";
import { registerListKey, scopedParams, type LlQueryKey } from "../queryKeys";
import { useBootstrap } from "./useBootstrap";
import { usePlanningScope } from "./usePlanningScope";
import { classifyProblem, type ClassifiedProblem } from "../../domain/problem";
import type { ClientRegisterConfig } from "../../domain/registerConfig";

export const PAGE_LIMIT = 500;

export interface ClientRegisterResult<Row> {
  rows: Row[];
  total: number;
  isLoading: boolean;
  isFetching: boolean;
  problem: ClassifiedProblem | null;
  refetch: () => void;
}

/**
 * The register machine is generic over `OperationId`, so it cannot name one call signature. This
 * is the single documented widening; `api` itself is the handed, fully typed client.
 */
type ListCall = (
  operationId: OperationId,
  args: { query: Record<string, unknown>; path?: Record<string, string> },
) => Promise<Page<unknown>>;

/** Drops empty filter values so a blank search box is not sent as `search=`. */
export function queryOf(params: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    out[key] = value;
  }
  return out;
}

/** The fetch-all helper: every page at `limit=500`, concatenated. NEVER used for products. */
export async function fetchAllPages<Row>(
  operation: OperationId,
  filters: Record<string, unknown>,
  registerKey: string,
  path?: Record<string, string>,
): Promise<{ rows: Row[]; total: number }> {
  const call = api.call.bind(api) as unknown as ListCall;
  const rows: Row[] = [];
  let total = 0;
  let offset = 0;

  /* Page until the concatenated set reaches `total`; the first answer names it. */
  for (;;) {
    const page = await call(operation, {
      query: { ...filters, limit: PAGE_LIMIT, offset },
      ...(path ? { path } : {}),
    });
    total = page.total;
    rows.push(...(page.items as Row[]));
    offset += PAGE_LIMIT;
    if (page.items.length === 0 || rows.length >= total) break;
  }

  if (import.meta.env.DEV && rows.length !== total) {
    throw new Error(
      `Client register "${registerKey}" loaded ${rows.length} rows but the server reports ${total}.`,
    );
  }

  return { rows, total };
}

/** The ONE shared read for a client-loaded list. */
export function clientListOptions<Row>(
  tenantKey: string | null,
  registerKey: string,
  operation: OperationId,
  filters: Record<string, unknown> = {},
  /** A path-scoped list (`{ run_id }`) and the key that scopes it to its own domain. */
  scope?: {
    path?: Record<string, string>;
    queryKey?: (tenantKey: string, params: Record<string, unknown>) => LlQueryKey;
    /**
     * A planning-scope-keyed list (PS-D10): the RESOLVED scope id goes into the key. `undefined`
     * is a tenant-wide list. The request's own `planningScopeId` is already in `filters`.
     */
    scopeId?: string | null;
  },
) {
  const keyParams = scope?.scopeId === undefined ? filters : scopedParams(filters, scope.scopeId);
  return queryOptions({
    queryKey:
      scope?.queryKey?.(tenantKey ?? "_", keyParams) ??
      registerListKey(tenantKey ?? "_", registerKey, keyParams),
    enabled: tenantKey !== null,
    queryFn: () => fetchAllPages<Row>(operation, filters, registerKey, scope?.path),
  });
}

export function useClientRegister<Row, Params extends Record<string, unknown>>(
  config: ClientRegisterConfig<Row, Params>,
): ClientRegisterResult<Row> {
  const { data: boot } = useBootstrap();
  const tenantKey = boot?.tenant.key ?? null;
  const { scopeId, requestParams } = usePlanningScope();
  const filters = queryOf(config.params as Record<string, unknown>);
  /* A filter may declare a wire conversion (a day becomes an instant); the server doctrine
     applies the same `toQuery` in `useServerRegister`. */
  for (const filter of config.filters) {
    if (!filter.toQuery) continue;
    const keys = filter.rangeKeys ? [filter.rangeKeys.from, filter.rangeKeys.to] : [filter.key];
    for (const key of keys) {
      const value = filters[key];
      if (typeof value === "string" && value) filters[key] = filter.toQuery(value, key);
    }
  }

  /* A scope-keyed list asks for the selected scope (nothing for the default one) and keys on it. */
  const scoped = config.scoped === true;
  if (scoped) Object.assign(filters, requestParams);

  const query = useQuery(
    clientListOptions<Row>(tenantKey, config.key, config.operation, filters, {
      ...(config.path ? { path: config.path } : {}),
      ...(config.queryKey ? { queryKey: config.queryKey } : {}),
      ...(scoped ? { scopeId } : {}),
    }),
  );

  return {
    rows: query.data?.rows ?? [],
    total: query.data?.total ?? 0,
    isLoading: query.isPending,
    isFetching: query.isFetching,
    problem: query.error ? classifyProblem(query.error) : null,
    refetch: () => void query.refetch(),
  };
}
